import { useEffect, useRef, useState } from "react";

/**
 * Attach `ref` to a section's root element. Returns true once that element
 * has entered the viewport, and stays true afterward (fires once).
 * Used to gate the `.reveal-scroll` entrance-animation classes via the
 * `.in-view` parent class — see index.css.
 */
export function useInView<T extends HTMLElement>(
  options?: IntersectionObserverInit
) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      // No element to observe (e.g. ref not attached) — never leave text
      // permanently hidden because of a wiring mistake.
      setInView(true);
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      // Fire while the section is still well below the fold (positive
      // bottom margin expands the trigger zone downward by roughly a full
      // viewport height) so the fade-in finishes *before* the text
      // actually scrolls into view. Sized to a viewport height so normal
      // scroll gestures — mouse wheel, trackpad flicks, Page Down,
      // spacebar — can't outrun the animation and catch text mid-fade.
      // (An instant, non-scrolled jump straight to a far-down anchor is
      // the one case this can't fully cover — the setTimeout/CSS
      // fallbacks below handle that so it's never worse than a brief
      // delay, never a permanent hide.)
      {
        threshold: 0,
        rootMargin: `0px 0px ${typeof window !== "undefined" ? window.innerHeight : 900}px 0px`,
        ...options,
      }
    );

    observer.observe(el);

    // Safety net: if for any reason the observer never fires for this
    // element (layout edge case, zero height at observe-time, etc.),
    // force the reveal after a few seconds rather than hide the text
    // forever.
    const fallback = setTimeout(() => setInView(true), 4000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return { ref, inView };
}
